// src/features/affiliate/PostbacksPanel.tsx
// Admin: postback URLs fired when a conversion changes status, per offer.
// "New"/"Edit" open a desktop-app create dialog (docs/ux/14-forms.md)
// instead of an always-visible inline form.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliatePostback, useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { FormDialog, TitleInput, FormRow, SegmentedControl } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { Postback } from '@core/types/affiliate';
import { ChipInput } from './ChipInput';
import { errMessage } from './format';

const TRIGGER_OPTIONS = ['pending', 'approved', 'rejected', 'hold'];

interface PostbacksPanelProps {
  offerId: string;
}

export const PostbacksPanel = observer(({ offerId }: PostbacksPanelProps) => {
  const { t } = useI18n();
  const store = useAffiliatePostback();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Postback | null | 'new'>(null);
  const [deleting, setDeleting] = useState<Postback | null>(null);

  const [name, setName] = useState('');
  const [urlTemplate, setUrlTemplate] = useState('');
  const [method, setMethod] = useState('GET');
  const [isActive, setIsActive] = useState(true);
  const [triggers, setTriggers] = useState<string[]>(['approved']);
  const [isSaving, setIsSaving] = useState(false);

  const load = () => {
    setError(null);
    store.ListPostbacks(offerId).catch((e: unknown) => setError(errMessage(e, t('affiliate.admin.postbacks.loadError'))));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  const openNew = () => {
    setName('');
    setUrlTemplate('');
    setMethod('GET');
    setIsActive(true);
    setTriggers(['approved']);
    setEditing('new');
  };

  const openEdit = (p: Postback) => {
    setName(p.name);
    setUrlTemplate(p.url_template);
    setMethod(p.method || 'GET');
    setIsActive(p.is_active);
    setTriggers(p.trigger_statuses);
    setEditing(p);
  };

  const isEdit = editing !== null && editing !== 'new';

  const save = async () => {
    if (!name.trim() || !urlTemplate.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (editing === 'new') {
        await store.CreatePostback(offerId, {
          entity: undefined,
          offer_id: offerId,
          name: name.trim(),
          url_template: urlTemplate.trim(),
          method,
          trigger_statuses: triggers,
          static_params: {},
          metadata: {},
        });
      } else if (editing?.entity) {
        await store.UpdatePostback(editing.entity.id, {
          entity: editing.entity,
          update_mask: ['name', 'url_template', 'method', 'is_active', 'trigger_statuses'],
          name: name.trim(),
          url_template: urlTemplate.trim(),
          method,
          is_active: isActive,
          trigger_statuses: triggers,
          static_params: editing.static_params ?? {},
          metadata: editing.metadata ?? {},
        });
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(errMessage(e, t('affiliate.admin.postbacks.saveError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-foreground">{t('affiliate.admin.postbacks.title')}</h4>
        <Button size="sm" variant="secondary" onClick={openNew}>{t('affiliate.admin.postbacks.new')}</Button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <ul className="space-y-1.5">
        {store.items.map((p) => (
          <li key={p.entity?.id} className="rounded-md border border-border p-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-foreground">{p.name}</span>
              <span className="flex items-center gap-1.5">
                <Badge variant={p.is_active ? 'success' : 'default'}>{p.is_active ? t('common.active') : t('common.inactive')}</Badge>
                <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-foreground">{t('common.edit')}</button>
                <button onClick={() => setDeleting(p)} className="text-muted-foreground hover:text-destructive">{t('common.delete')}</button>
              </span>
            </div>
            <p className="mt-0.5 truncate font-mono text-muted-foreground">{p.method} {p.url_template}</p>
          </li>
        ))}
        {store.items.length === 0 && <p className="text-xs text-muted-foreground">{t('affiliate.admin.postbacks.empty')}</p>}
      </ul>

      <FormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        onSubmit={save}
        submitLabel={isEdit ? t('common.saveChanges') : t('common.create')}
        canSubmit={!!name.trim() && !!urlTemplate.trim()}
        isSubmitting={isSaving}
        dirty={!!(name.trim() || urlTemplate.trim())}
        width="md"
        aria-label={isEdit ? t('common.edit') : t('affiliate.admin.postbacks.new')}
      >
        <div className="px-3.5 pb-1 pt-2">
          <TitleInput value={name} onChange={setName} placeholder={`${t('common.name')}…`} initialFocus aria-label={t('common.name')} />
        </div>
        <div className="space-y-1 px-3.5 pb-3">
          <FormRow label={t('affiliate.admin.postbacks.urlTemplate')} labelWidth="sm">
            <input
              value={urlTemplate}
              onChange={(e) => setUrlTemplate(e.target.value)}
              placeholder="https://tracker.com/postback?clickid={click_id}&payout={payout}"
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-xs text-foreground outline-none focus:ring-2 focus:ring-ring"
            />
          </FormRow>
          <FormRow label={t('affiliate.admin.postbacks.method')} labelWidth="sm">
            <SegmentedControl
              size="sm"
              value={method}
              onChange={setMethod}
              options={[{ value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' }]}
              aria-label={t('affiliate.admin.postbacks.method')}
            />
          </FormRow>
          {isEdit && (
            <FormRow label={t('common.active')} labelWidth="sm">
              <ToggleSwitch label="" checked={isActive} onChange={setIsActive} />
            </FormRow>
          )}
          <FormRow label={t('affiliate.admin.postbacks.triggerStatuses')} labelWidth="sm">
            <ChipInput value={triggers} onChange={setTriggers} placeholder={TRIGGER_OPTIONS.join(', ')} aria-label={t('affiliate.admin.postbacks.triggerStatuses')} />
          </FormRow>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deleting}
        title={t('affiliate.admin.postbacks.deleteTitle')}
        destructive
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting?.entity?.id) return;
          try {
            await store.DeletePostback(deleting.entity.id);
            setDeleting(null);
            load();
          } catch (e) {
            setError(errMessage(e, t('affiliate.admin.postbacks.saveError')));
            setDeleting(null);
          }
        }}
      />
    </div>
  );
});
